import React, { FunctionComponent } from 'react';
import { FitnessActivity } from './FitnessActivity';
import { FitnessTable } from './FitnessTable';

import { notNull } from '../../../utils/typeGuards';
import styles from './FitnessMessage.scss';
import {
  FitnessActivity as FitnessActivityData,
  Distance,
  Duration,
  DistanceUnit,
  GetStravaMeasurementSystemQuery,
} from '../../../../types/__generated__';
import { getMeasurementSystemFallback } from '../../../utils/measurementSystem';
import { useTranslation } from 'react-i18next';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import getStravaMeasurementSystem from '../StravaTimelineEntry/GetStravaMeasurementSystem.gql';

interface Props {
  message: string | null | undefined;
  caption: string;
  fitness: FitnessActivityData;
}

function displayPreferenceGuard(
  displayValue: any,
  value?: Duration | Distance | null,
) {
  return (value && displayValue) ?? null;
}

export const FitnessMessage: FunctionComponent<Props> = ({
  message,
  caption,
  fitness: { activityType, duration, distance, elevation, title = '' },
}) => {
  const { data: settingsData } = useFullPageQuery<
    GetStravaMeasurementSystemQuery
  >(getStravaMeasurementSystem);
  const measurementSystem =
    settingsData?.page?.strava?.settings?.measurementSystem ||
    getMeasurementSystemFallback();
  const { t } = useTranslation('common', { useSuspense: false });

  const headings = [
    displayPreferenceGuard(t('settings.fitness.showDuration'), duration),
    displayPreferenceGuard(t('settings.fitness.showDistance'), distance),
    displayPreferenceGuard(t('settings.fitness.showElevation'), elevation),
  ].filter(notNull);

  const elevationElement =
    elevation &&
    measurementSystem &&
    `${elevation.value} ${distanceUnitMap[elevation.unit]}`;

  const rows = [
    [
      displayPreferenceGuard(duration, duration),
      displayPreferenceGuard(distance, distance),
      displayPreferenceGuard(elevationElement, elevation),
    ],
  ].map(row => row.filter(notNull));

  return (
    <div>
      {activityType ? (
        <FitnessActivity activityType={activityType} title={title ?? ''} />
      ) : null}
      <div className={styles.message}>{message}</div>
      <FitnessTable caption={caption} headings={headings} rows={rows} />
    </div>
  );
};

const distanceUnitMap = {
  [DistanceUnit.Metres]: 'm',
  [DistanceUnit.Feet]: 'ft',
};
